/**
 * Shared pieces of the Mark-as-Sold / Edit Sale Details flow used by both
 * property pages (CRM /properties/:id and Collings /collings-properties/:id).
 *
 * A sold property's details (price, sold date, settlement, split, buyers)
 * could only be fixed by marking it unsold and sold again, which wiped the
 * sold date and re-created the commission. Edit Sale Details re-submits the
 * same dialog with is_sold still true; the backend treats that as a
 * correction and updates the existing records in place.
 */

/** Today's date as YYYY-MM-DD in the browser's local timezone. */
export const localToday = () => {
  const d = new Date();
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().split("T")[0];
};

/** The property's contract (sale-agreed) date as YYYY-MM-DD, or "". */
export const contractDateOf = (property) =>
  String(property?.contract_date || property?.extra_info?.contract_date || "").slice(0, 10);

/**
 * The sold date the dialog should start on when the property has none yet:
 * the contract date when one is recorded, otherwise blank so the agent has to
 * pick it. A silent "today" is how a September date reached realestate.com.au
 * for a sale agreed in October, and REA won't accept a correction.
 */
export const defaultSoldDate = (property) => contractDateOf(property);

const DAY_MS = 24 * 60 * 60 * 1000;
const daysBetween = (a, b) => Math.round((new Date(a) - new Date(b)) / DAY_MS);
const formatDay = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

/**
 * Warnings (not blocks) for sale dates that look wrong. Dates are YYYY-MM-DD.
 *
 * @param {object} args
 * @param {string} args.soldDate
 * @param {string} [args.settlementDate]
 * @param {string} [args.contractDate]
 * @param {boolean} [args.isEdit]  correcting an older sale: skip the "long ago" check
 * @param {string} [args.today]    for tests; defaults to localToday()
 */
export const saleDateWarnings = ({ soldDate, settlementDate, contractDate, isEdit = false, today = localToday() }) => {
  const warnings = [];
  if (!soldDate) return warnings;
  if (soldDate > today) {
    warnings.push("The sold date is in the future.");
  }
  if (settlementDate && settlementDate < soldDate) {
    warnings.push("Settlement is before the sold date.");
  }
  if (contractDate) {
    if (Math.abs(daysBetween(soldDate, contractDate)) > 7) {
      warnings.push(
        `The sold date is more than a week from the contract date (${formatDay(contractDate)}).`,
      );
    }
  } else if (!isEdit && daysBetween(today, soldDate) > 7) {
    warnings.push("The sold date is more than a week ago. Check it is the date the sale was agreed.");
  }
  return warnings;
};

/**
 * PATCH body for a Mark-as-Sold or Edit Sale Details submission.
 *
 * @param {object} soldDetails  what MarkSoldModal hands to onConfirm
 * @param {object} [opts]
 * @param {boolean} [opts.withPurchasers]  CRM properties store purchasers (sent as JSON because the request is FormData)
 * @param {boolean} [opts.withPortalFlag]  Collings properties carry the sold-through-portal tick in the same request
 */
export const buildSaleDetailsPatch = (soldDetails, { withPurchasers = false, withPortalFlag = false } = {}) => {
  const data = {
    is_sold: true,
    sold_price: soldDetails.soldPrice,
    suppress_sold_price: soldDetails.suppressSoldPrice,
    // Shown online as the "Sold on" date and the leaderboard dating fallback.
    sold_date: soldDetails.soldDate || localToday(),
    // Distinct key so it isn't confused with the secondary-agent PATCH branch.
    commission_co_agent_id: soldDetails.coAgentId || "",
    commission_split_percentage: soldDetails.splitPercentage,
    // 0%/0% no-credit sale: recorded but excluded from both agents' GCI/leaderboard.
    no_commission_credit: soldDetails.noCommissionCredit ? "true" : "false",
  };
  if (soldDetails.settlementDate) {
    data.settlement_date = soldDetails.settlementDate;
  }
  if (withPortalFlag) {
    data.sold_through_collings_portal = Boolean(soldDetails.soldThroughCollingsPortal);
  }
  // Only send purchasers when the agent entered someone, so a save without
  // buyers doesn't wipe existing purchasers.
  if (withPurchasers && Array.isArray(soldDetails.purchasers) && soldDetails.purchasers.length) {
    data.purchasers = JSON.stringify(soldDetails.purchasers);
  }
  return data;
};
